// components/SEO/VideoSEO.tsx
import { Helmet } from 'react-helmet-async';

interface VideoSEOProps {
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl: string;
  duration?: string;
  publishedTime: string;
  author?: string;
  canonicalUrl: string;
  embedUrl?: string;
}

const VideoSEO: React.FC<VideoSEOProps> = ({
  title,
  description,
  videoUrl,
  thumbnailUrl,
  duration,
  publishedTime,
  author,
  canonicalUrl,
  embedUrl
}) => {
  const siteName = 'StrymHub';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title} | {siteName}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="video.other" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={thumbnailUrl} />
      <meta property="og:video" content={videoUrl} />
      <meta property="og:video:secure_url" content={videoUrl} />
      <meta property="og:video:type" content="video/mp4" />
      <meta property="og:video:width" content="1280" />
      <meta property="og:video:height" content="720" />
      <meta property="og:site_name" content={siteName} />
      {duration && <meta property="video:duration" content={duration} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="player" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={thumbnailUrl} />
      {embedUrl && <meta name="twitter:player" content={embedUrl} />}
      <meta name="twitter:player:width" content="1280" />
      <meta name="twitter:player:height" content="720" />

      {/* Additional Video-specific Tags */}
      <meta name="video" content={videoUrl} />
      <meta name="thumbnail" content={thumbnailUrl} />
      
      {/* Rich Snippets */}
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description} />
      <meta itemProp="image" content={thumbnailUrl} />
      <meta itemProp="contentUrl" content={videoUrl} />
      <meta itemProp="embedUrl" content={embedUrl || canonicalUrl} />
      <meta itemProp="uploadDate" content={publishedTime} />
      {duration && <meta itemProp="duration" content={duration} />}

      {/* Search Engine Directives */}
      <meta name="robots" content="index, follow, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-video-preview:-1" />
      
      {/* Additional SEO hints */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      
      {/* Link tags for video discovery */}
      <link rel="alternate" type="application/json+oembed" href={`https://strymhub.com/oembed?url=${encodeURIComponent(canonicalUrl)}`} />
    </Helmet>
  );
};

export default VideoSEO;